import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@shared/store/dialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import Dialog from "@shared/ui/molecules/Dialog";
import { Column, SpaceBetweenRow } from "@shared/ui/atoms/StructuralLayout";
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import {
  loadFavouriteOffices,
  selectAllOfficeEntities,
  selectFavouriteDesks
} from '@features/desks/store/advanceHotDeskingSetup.duck';
import { FavouriteDesk, OfficeEntity, toggleFavouriteDesk } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from "@shared/ui/atoms/fonts/Body";
import { findOfficeInOffices, getFullOfficeTitle } from '@shared/utils/OfficeHelper';
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { Desk, getDesksInOffice } from '@features/desks/services/DeskBookingService';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { LoadingOverlay } from "@shared/ui/molecules/LoadingOverlay";
import { FavouriteImage } from "@shared/ui/atoms/FavouriteImage";
import OfficeLocationContentCard from "@shared/ui/molecules/OfficeLocationContentCard";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export default function ManageFavouritesDialog() {
  const dispatch = useDispatch();
  const [selectedOfficeId, setSelectedOfficeId] = useState(0);
  const [desks, setDesks] = useState<Desk[]>([]);
  const [loading, setLoading] = useState(false);

  const allOffices = useSelector(selectAllOfficeEntities);
  const favoriteDesks = useSelector(selectFavouriteDesks);

  const selectedOffice = useMemo(() => findOfficeInOffices(allOffices, selectedOfficeId), [selectedOfficeId, allOffices]);

  const visibleOffices = useMemo(() => {
    if (selectedOffice) {
      return allOffices.filter((o: OfficeEntity) => o.parentId === selectedOffice.id)
    } else {
      return allOffices.filter((o: OfficeEntity) => !o.parentId)
    }
  }, [allOffices, selectedOffice]);

  const breadCrumb = useMemo(() => {
    if (selectedOffice) {
      return getFullOfficeTitle(selectedOffice, allOffices)
    }
  }, [selectedOffice, allOffices])

  useEffect(() => {
    if (selectedOffice?.deskBookingEnabled) {
      setLoading(true);
      setDesks([]);
      getDesksInOffice(selectedOffice.id)
        .then((resp: Desk[]) => naturalSort(resp, 'label'))
        .then((resp: Desk[]) => setDesks(resp))
        .finally(() => setLoading(false))
    } else {
      setDesks([]);
    }
  }, [selectedOffice]);


  const close = () => {
    dispatch(closeDialog());
  }

  const isDeskFavoured = (desk: Desk): boolean => {
    return !!favoriteDesks.find((foe: FavouriteDesk) => foe.deskId === desk.id);
  }

  const isDeskAssigned = (desk: Desk): boolean => {
    return !!favoriteDesks.find((foe: FavouriteDesk) => foe.deskId === desk.id)?.assigned;
  }

  const onDeskFavoured = async (desk: Desk) => {
    if (isDeskAssigned(desk)) {
      return;
    }
    try {
      setLoading(true);
      await toggleFavouriteDesk(desk.id);
      await new Promise((resolve: any) => setTimeout(() => resolve(), 250));
      dispatch(loadFavouriteOffices());
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog isOpen={true} onClose={close} showLogo={true}>
      {loading && <LoadingOverlay />}
      <DialogHeader title="dialog-headers.manage-favourites" />

      {!!breadCrumb && <SpaceBetweenRow>
        <BodyRegular>{breadCrumb}</BodyRegular>
        <OutlineButton size={ButtonSize.Small}
                       click={() => setSelectedOfficeId(0)}
                       style={{marginBottom: 16, maxWidth: 80}}
                       text="Reset" />
      </SpaceBetweenRow>}

      {visibleOffices.length === 0 && desks.length === 0 && !loading && !!selectedOffice && <>
        <OfficeLocationContentCard office={selectedOffice} onClick={()=>{}} />
      </>}

      <Column>
        {visibleOffices.map((office: OfficeEntity, key: number) => <OfficeLocationContentCard office={office}
                                                                                              key={key}
                                                                                              onClick={() => setSelectedOfficeId(office.id)} />)}

        {desks.map((desk: Desk, key: number) => <SpaceBetweenRow key={key}>
          <BodyRegular weight={600}>{desk.label}</BodyRegular>
          <FavouriteImage src={isDeskFavoured(desk) ? '/assets/icons/StarActive.svg' : '/assets/icons/Star.svg'}
                          onClick={() => onDeskFavoured(desk)}
                          alt="Favourite desk"/>
        </SpaceBetweenRow>)}
      </Column>

      <Column>

        <PrimaryButton fullWidth={true}
                       click={close}
                       text="button.close" />
      </Column>
    </Dialog>
  )
}

